import type { AdapterInitConfig, ProductLineDeclaration } from './AdapterRegistry';

const REGISTERED_AUTH_TYPES = ['service-auth'] as const;
const REGISTERED_BFF_TYPES = ['rest', 'esa-rest', 'graphql'] as const;

/** Thrown when product-line declarations fail structural validation. */
export class ConfigValidationError extends Error {
  constructor(
    message: string,
    readonly details?: string[]
  ) {
    super(message);
    this.name = 'ConfigValidationError';
  }
}

function requireString(
  obj: Record<string, unknown>,
  field: string,
  path: string,
  errors: string[]
): void {
  const value = obj[field];
  if (typeof value !== 'string' || value.trim() === '') {
    errors.push(`${path}.${field} is required`);
  }
}

function validateAuthConfig(pl: ProductLineDeclaration, errors: string[]): void {
  const auth = pl.auth;
  const path = `productLines[${pl.id}].auth`;

  if (!REGISTERED_AUTH_TYPES.includes(auth.type as (typeof REGISTERED_AUTH_TYPES)[number])) {
    errors.push(
      `${path}.type "${auth.type}" is not registered. Expected one of: ${REGISTERED_AUTH_TYPES.join(', ')}`
    );
    return;
  }

  if (auth.type === 'service-auth') {
    requireString(auth as Record<string, unknown>, 'serviceUrl', path, errors);
    const endpoints = auth.endpoints;
    if (!endpoints || typeof endpoints !== 'object') {
      errors.push(`${path}.endpoints is required`);
      return;
    }
    const ep = endpoints as unknown as Record<string, unknown>;
    requireString(ep, 'session', `${path}.endpoints`, errors);
    requireString(ep, 'logout', `${path}.endpoints`, errors);
  }
}

function validateBffConfig(pl: ProductLineDeclaration, errors: string[]): void {
  const bff = pl.bff;
  const path = `productLines[${pl.id}].bff`;

  if (!REGISTERED_BFF_TYPES.includes(bff.type as (typeof REGISTERED_BFF_TYPES)[number])) {
    errors.push(
      `${path}.type "${bff.type}" is not registered. Expected one of: ${REGISTERED_BFF_TYPES.join(', ')}`
    );
    return;
  }

  requireString(bff as Record<string, unknown>, 'baseUrl', path, errors);
  const converseUrl = (bff as Record<string, unknown>).converseUrl;
  if (converseUrl !== undefined && (typeof converseUrl !== 'string' || converseUrl.trim() === '')) {
    errors.push(`${path}.converseUrl must be a non-empty string when set`);
  }
}


/**
 * Validate AdapterInitConfig before adapter instantiation.
 */
export function validateProductLineConfig(cfg: AdapterInitConfig): void {
  const errors: string[] = [];

  if (!cfg.productLines?.length) {
    throw new ConfigValidationError(
      '[adapters] No product-line declarations available. ' +
        'Set UI_PRODUCT_LINES in public/envConfig.js, or set UI_PKS_URL ' +
        'to point to a running gaxl-platform-knowledge-service instance.'
    );
  }

  const seenIds = new Set<string>();
  for (const pl of cfg.productLines) {
    if (!pl.id?.trim()) {
      errors.push('Each product line must have a non-empty id');
      continue;
    }
    if (seenIds.has(pl.id)) {
      errors.push(`Duplicate product line id: ${pl.id}`);
    }
    seenIds.add(pl.id);
    validateAuthConfig(pl, errors);
    validateBffConfig(pl, errors);
  }

  if (errors.length) {
    throw new ConfigValidationError('Product-line configuration validation failed', errors);
  }
}
